import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { CompanyAutocomplete } from "@/components/CompanyAutocomplete";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import {
  Store, Plus, Minus, X, Download, FileText, ShoppingCart, Pencil, Trash2,
  Wallet, QrCode, CreditCard, Search, ImageIcon,
} from "lucide-react";

const emptyForm = {
  client_nom: "", client_prenom: "", client_telephone: "", client_email: "",
  adresse: "", ville: "", code_postal: "", canal: "B2C", notes: "",
  mode_paiement: "liquide", virement_beneficiaire: "", source_acquisition: "",
};

const SOURCES_ACQUISITION = ["Facebook", "Instagram", "TikTok", "Google", "Bouche-à-oreille", "Ancien client", "Autre"];

const PAYMENT_MODES = [
  { key: "liquide",  label: "Cash",     icon: Wallet,     tone: "bg-emerald-500 hover:bg-emerald-600" },
  { key: "qr",       label: "QR code",  icon: QrCode,     tone: "bg-indigo-500 hover:bg-indigo-600" },
  { key: "terminal", label: "Terminal", icon: CreditCard, tone: "bg-slate-700 hover:bg-slate-800" },
  { key: "virement", label: "Virement", icon: FileText,   tone: "bg-amber-500 hover:bg-amber-600" },
];

const VenteDepot = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [produits, setProduits] = useState([]);
  const [ventes, setVentes] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [cart, setCart] = useState([]); // { produit_id, sku, modele, couleur, quantite, prix_unitaire, image_url }
  const [bumpSku, setBumpSku] = useState(null); // Anime "+1" éphémère sur la carte
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [productQuery, setProductQuery] = useState("");
  const [showClientForm, setShowClientForm] = useState(false);

  const fetchAll = async () => {
    try {
      const [p, v] = await Promise.all([
        axios.get(`${API}/produits`, { headers }),
        axios.get(`${API}/ventes-depot`, { headers }),
      ]);
      setProduits(p.data);
      setVentes(v.data);
    } catch { toast.error("Erreur de chargement"); }
  };
  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredProduits = useMemo(() => {
    const q = productQuery.trim().toLowerCase();
    return produits
      .filter(p => !q || (`${p.modele} ${p.couleur} ${p.sku}`).toLowerCase().includes(q));
  }, [produits, productQuery]);

  const total = cart.reduce((a, it) => a + (parseFloat(it.prix_unitaire) || 0) * (parseInt(it.quantite) || 0), 0);
  const totalItems = cart.reduce((a, it) => a + (parseInt(it.quantite) || 0), 0);

  const addProductToCart = (prod) => {
    setCart((prev) => {
      const idx = prev.findIndex(x => x.sku === prod.sku);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantite: next[idx].quantite + 1 };
        return next;
      }
      return [...prev, {
        produit_id: prod.id, sku: prod.sku, modele: prod.modele,
        couleur: prod.couleur, quantite: 1,
        prix_unitaire: prod.prix_b2c_ttc || 0,
        image_url: prod.image_url || "",
      }];
    });
  };

  const changeQty = (sku, delta) => setCart((prev) => prev
    .map(it => it.sku === sku ? { ...it, quantite: Math.max(1, it.quantite + delta) } : it)
  );
  const setPrix = (sku, val) => setCart((prev) => prev
    .map(it => it.sku === sku ? { ...it, prix_unitaire: val } : it)
  );
  const removeFromCart = (sku) => setCart((prev) => prev.filter(it => it.sku !== sku));

  const resetForm = () => { setForm(emptyForm); setCart([]); setEditingId(null); setShowClientForm(false); };

  const startEdit = (v) => {
    setEditingId(v.id);
    setForm({
      client_nom: v.client_nom || "", client_prenom: v.client_prenom || "", client_telephone: v.client_telephone || "",
      client_email: v.client_email || "", adresse: (v.adresse === "Vente sur place (dépôt)" ? "" : v.adresse) || "",
      ville: v.ville || "", code_postal: v.code_postal || "", canal: v.canal || "B2C", notes: v.notes || "",
      mode_paiement: v.mode_paiement || "liquide",
      virement_beneficiaire: v.virement_beneficiaire || "",
      source_acquisition: v.source_acquisition || "",
    });
    const pls = (v.produits_lies || []).map((pl) => {
      const prod = produits.find((x) => x.sku === pl.sku);
      return {
        produit_id: prod?.id || "", sku: pl.sku, modele: pl.modele, couleur: pl.couleur,
        quantite: pl.quantite || 1, prix_unitaire: pl.prix_unitaire || 0,
        image_url: prod?.image_url || "",
      };
    });
    setCart(pls);
    setShowClientForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette vente dépôt ? Le stock sera réintégré.")) return;
    try {
      await axios.delete(`${API}/ventes-depot/${id}`, { headers });
      toast.success("Vente supprimée — stock réintégré");
      if (editingId === id) resetForm();
      fetchAll();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur lors de la suppression"); }
  };

  const validateSale = async (modePaiement) => {
    if (cart.length === 0) { toast.error("Le panier est vide"); return; }
    if (!form.client_nom.trim()) {
      toast.error("Nom du client obligatoire — clique « Ajouter client »");
      setShowClientForm(true);
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      mode_paiement: modePaiement,
      virement_beneficiaire: modePaiement === "virement" ? form.virement_beneficiaire : "",
      produits: cart.map((it) => ({
        sku: it.sku, modele: it.modele, couleur: it.couleur,
        quantite: parseInt(it.quantite) || 1,
        prix_unitaire: parseFloat(it.prix_unitaire) || 0,
      })),
    };
    try {
      if (editingId) {
        const res = await axios.put(`${API}/ventes-depot/${editingId}`, payload, { headers });
        toast.success(`Vente modifiée — ${res.data.prix_total.toFixed(2)} €`);
      } else {
        const res = await axios.post(`${API}/ventes-depot`, payload, { headers });
        toast.success(`✅ Vente encaissée — ${res.data.prix_total.toFixed(2)} € (${modePaiement})`);
      }
      resetForm();
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'encaissement");
    } finally { setSaving(false); }
  };

  const downloadRecu = async (id) => {
    try {
      const res = await axios.get(`${API}/livraisons/${id}/recu`, { headers, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url; link.setAttribute("download", `recu_depot_${id.slice(0, 8)}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Erreur lors du téléchargement du reçu"); }
  };

  const createFacture = async (id) => {
    try {
      const res = await axios.post(`${API}/livraisons/transform-to-facture`, { ids: [id] }, { headers });
      const f = (res.data.factures || [])[0];
      toast.success(`Facture créée${f?.numero ? " n°" + f.numero : ""} — voir Devis/Factures`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur création facture"); }
  };

  return (
    <div className="space-y-6" data-testid="vente-depot-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 flex items-center gap-2">
          <Store className="w-6 h-6 sm:w-7 sm:h-7 text-indigo-600" /> Caisse — Vente sur place
        </h1>
        <p className="text-slate-500 mt-1 text-sm">Clique sur un produit pour l&apos;ajouter au panier, puis choisis un mode de paiement.</p>
      </div>

      {/* POS grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* LEFT — Product tiles (2/3) */}
        <div className="lg:col-span-2 space-y-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={productQuery}
              onChange={(e) => setProductQuery(e.target.value)}
              placeholder="Rechercher un modèle, couleur, SKU…"
              className="pl-9"
              data-testid="pos-product-search"
            />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="pos-product-grid">
            {filteredProduits.map((p) => {
              const out = p.stock_restant <= 0;
              const inCart = cart.find((c) => c.sku === p.sku);
              const bumping = bumpSku === p.sku;
              const quickAdd = (e) => {
                e.stopPropagation();
                addProductToCart(p);
                setBumpSku(p.sku);
                setTimeout(() => setBumpSku((s) => (s === p.sku ? null : s)), 800);
              };
              const quickDec = (e) => {
                e.stopPropagation();
                if (!inCart) return;
                if (inCart.quantite <= 1) removeFromCart(p.sku);
                else changeQty(p.sku, -1);
              };
              return (
                <button
                  key={p.id}
                  disabled={out}
                  onClick={() => addProductToCart(p)}
                  className={`group relative flex flex-col rounded-xl overflow-hidden border border-slate-200 bg-white shadow-sm text-left transition ${out ? "opacity-40 cursor-not-allowed" : "hover:shadow-md hover:-translate-y-0.5 hover:border-indigo-300"}`}
                  data-testid={`pos-tile-${p.sku}`}
                >
                  <div className="aspect-square w-full bg-slate-50 flex items-center justify-center overflow-hidden">
                    {p.image_url ? (
                      <img src={p.image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    ) : (
                      <ImageIcon className="w-8 h-8 text-slate-300" />
                    )}
                  </div>
                  <div className="p-2 space-y-0.5">
                    <p className="font-semibold text-xs sm:text-sm text-slate-800 truncate">{p.modele}</p>
                    <p className="text-[10px] sm:text-xs text-slate-500 truncate">{p.couleur}</p>
                    <div className="flex items-center justify-between pt-1">
                      <span className="text-xs sm:text-sm font-bold text-indigo-600">{(p.prix_b2c_ttc || 0).toFixed(0)} €</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${p.stock_restant > 5 ? "bg-emerald-100 text-emerald-700" : p.stock_restant > 0 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-600"}`}>
                        {out ? "Rupture" : `Stock ${p.stock_restant}`}
                      </span>
                    </div>
                  </div>

                  {/* Badge "+1" éphémère */}
                  {bumping && (
                    <span
                      className="pointer-events-none absolute right-2 bottom-14 text-[11px] font-bold text-white bg-indigo-600 rounded-full px-2 py-0.5 shadow-md pos-bump-badge"
                      aria-hidden
                    >
                      +1
                    </span>
                  )}

                  {/* Contrôle quick-add — coin bas droit */}
                  {!out && (
                    inCart ? (
                      <div
                        className="absolute right-1.5 bottom-1.5 flex items-center gap-0.5 bg-indigo-600 text-white rounded-full shadow-md pl-0.5 pr-0.5"
                        role="group"
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`pos-card-qty-${p.sku}`}
                      >
                        <button
                          type="button"
                          onClick={quickDec}
                          className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-indigo-500 transition"
                          data-testid={`pos-card-minus-${p.sku}`}
                          aria-label="Retirer un exemplaire"
                        >
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <span className="min-w-[16px] text-center text-sm font-bold px-1" data-testid={`pos-card-qty-value-${p.sku}`}>{inCart.quantite}</span>
                        <button
                          type="button"
                          onClick={quickAdd}
                          className={`w-8 h-8 rounded-full flex items-center justify-center hover:bg-indigo-500 transition ${bumping ? "pos-bump" : ""}`}
                          data-testid={`pos-card-plus-${p.sku}`}
                          aria-label="Ajouter un exemplaire"
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={quickAdd}
                        className={`absolute right-2 bottom-2 w-10 h-10 rounded-full bg-indigo-600 hover:bg-indigo-700 hover:scale-110 text-white flex items-center justify-center shadow-md transition-transform ${bumping ? "pos-bump" : ""}`}
                        data-testid={`pos-card-add-${p.sku}`}
                        aria-label={`Ajouter ${p.modele}`}
                      >
                        <Plus className="w-5 h-5" />
                      </button>
                    )
                  )}
                </button>
              );
            })}
            {filteredProduits.length === 0 && (
              <p className="col-span-full text-center text-slate-400 py-8 text-sm">Aucun produit ne correspond à cette recherche.</p>
            )}
          </div>
        </div>

        {/* RIGHT — Cart + payment (1/3) */}
        <div className="space-y-3">
          <Card className="border-indigo-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5 text-indigo-600" /> Panier
                </span>
                <span className="text-sm font-normal text-slate-500">{totalItems} article{totalItems > 1 ? "s" : ""}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {cart.length === 0 ? (
                <p className="text-slate-400 text-sm text-center py-6">Ajoute des produits en cliquant sur les vignettes.</p>
              ) : (
                <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="pos-cart">
                  {cart.map((it) => (
                    <div key={it.sku} className="flex items-center gap-2 bg-slate-50 rounded-lg p-2" data-testid={`pos-cart-item-${it.sku}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{it.modele}</p>
                        <p className="text-xs text-slate-500 truncate">{it.couleur}</p>
                        <div className="flex items-center gap-1 mt-1">
                          <Input
                            type="number" step="0.01" min="0"
                            value={it.prix_unitaire}
                            onChange={(e) => setPrix(it.sku, e.target.value === "" ? "" : parseFloat(e.target.value))}
                            className="h-7 text-xs w-20"
                            data-testid={`pos-cart-prix-${it.sku}`}
                          />
                          <span className="text-xs text-slate-400">€/u</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => changeQty(it.sku, -1)} data-testid={`pos-cart-minus-${it.sku}`}><Minus className="w-3 h-3" /></Button>
                        <span className="w-6 text-center text-sm font-semibold">{it.quantite}</span>
                        <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => changeQty(it.sku, 1)} data-testid={`pos-cart-plus-${it.sku}`}><Plus className="w-3 h-3" /></Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => removeFromCart(it.sku)} data-testid={`pos-cart-remove-${it.sku}`}><X className="w-3 h-3" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="border-t pt-3 flex items-baseline justify-between">
                <span className="text-slate-600">Total</span>
                <span className="text-2xl font-bold text-indigo-700" data-testid="pos-total">{total.toFixed(2)} €</span>
              </div>
            </CardContent>
          </Card>

          {/* Client & payment */}
          <Card>
            <CardContent className="pt-4 space-y-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowClientForm(!showClientForm)}
                className="w-full justify-between"
                data-testid="pos-toggle-client"
              >
                <span>{form.client_nom || "+ Ajouter client"}</span>
                <span className="text-slate-400 text-xs">{showClientForm ? "−" : "+"}</span>
              </Button>
              {showClientForm && (
                <div className="space-y-2 border-t pt-3">
                  <CompanyAutocomplete
                    compact
                    onSelect={(c) => setForm((prev) => ({
                      ...prev,
                      client_nom: c.nom || prev.client_nom,
                      client_societe: c.nom || prev.client_societe,
                      client_tva: c.tva || prev.client_tva,
                      adresse: c.adresse || prev.adresse,
                      code_postal: c.code_postal || prev.code_postal,
                      ville: c.ville || prev.ville,
                      canal: "B2B",
                    }))}
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Nom *" value={form.client_nom} onChange={(e) => setForm({ ...form, client_nom: e.target.value })} data-testid="depot-nom-input" />
                    <Input placeholder="Prénom" value={form.client_prenom} onChange={(e) => setForm({ ...form, client_prenom: e.target.value })} data-testid="depot-prenom-input" />
                  </div>
                  <Input placeholder="Téléphone" value={form.client_telephone} onChange={(e) => setForm({ ...form, client_telephone: e.target.value })} data-testid="depot-tel-input" />
                  <Input placeholder="Email" type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} data-testid="depot-email-input" />
                  <div className="grid grid-cols-2 gap-2">
                    <Select value={form.canal} onValueChange={(v) => setForm({ ...form, canal: v })}>
                      <SelectTrigger data-testid="depot-canal-select" className="text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="B2C">B2C (TTC)</SelectItem>
                        <SelectItem value="B2B">B2B (HT)</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={form.source_acquisition || ""} onValueChange={(v) => setForm({ ...form, source_acquisition: v })}>
                      <SelectTrigger data-testid="depot-source-select" className="text-xs"><SelectValue placeholder="Source…" /></SelectTrigger>
                      <SelectContent>
                        {SOURCES_ACQUISITION.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Beneficiary select for virement */}
              {form.mode_paiement === "virement" && (
                <Select value={form.virement_beneficiaire || ""} onValueChange={(v) => setForm({ ...form, virement_beneficiaire: v })}>
                  <SelectTrigger data-testid="depot-virement-beneficiaire-select"><SelectValue placeholder="Bénéficiaire virement…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cocoon_elec">Cocoon-Elec</SelectItem>
                    <SelectItem value="livreur">Livreur</SelectItem>
                  </SelectContent>
                </Select>
              )}

              {/* Payment mode buttons */}
              <div className="grid grid-cols-2 gap-2 pt-2">
                {PAYMENT_MODES.map((m) => {
                  const Icon = m.icon;
                  return (
                    <Button
                      key={m.key}
                      type="button"
                      disabled={saving || cart.length === 0}
                      onClick={() => {
                        setForm((f) => ({ ...f, mode_paiement: m.key }));
                        validateSale(m.key);
                      }}
                      className={`${m.tone} text-white h-14 flex-col gap-1 shadow-md`}
                      data-testid={`pos-pay-${m.key}`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="text-xs font-semibold">{m.label}</span>
                    </Button>
                  );
                })}
              </div>
              {editingId && (
                <Button type="button" variant="outline" onClick={resetForm} className="w-full" data-testid="depot-cancel-edit-btn">Annuler la modification</Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Recent sales */}
      <Card>
        <CardHeader><CardTitle className="text-lg">Ventes récentes</CardTitle></CardHeader>
        <CardContent>
          {ventes.length === 0 ? (
            <p className="text-slate-400 text-sm">Aucune vente sur place pour l&apos;instant.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Client</TableHead><TableHead>Articles</TableHead><TableHead>Total</TableHead><TableHead>Paiement</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ventes.map((v) => (
                    <TableRow key={v.id} data-testid={`depot-row-${v.id}`}>
                      <TableCell className="font-medium">{v.client_nom}</TableCell>
                      <TableCell className="text-sm text-slate-600">{v.details_commande}</TableCell>
                      <TableCell className="whitespace-nowrap">{(v.prix || 0).toFixed(2)} €</TableCell>
                      <TableCell className="text-xs">{v.mode_paiement || "—"}</TableCell>
                      <TableCell className="text-xs text-slate-400 whitespace-nowrap">{(v.date_livraison_effective || "").slice(0, 10)}</TableCell>
                      <TableCell className="text-right space-x-1 whitespace-nowrap">
                        <Button size="sm" variant="outline" onClick={() => downloadRecu(v.id)} data-testid={`depot-recu-${v.id}`}><Download className="w-4 h-4 mr-1" />Reçu</Button>
                        <Button size="sm" variant="outline" onClick={() => createFacture(v.id)} data-testid={`depot-facture-${v.id}`}><FileText className="w-4 h-4 mr-1" />Facture</Button>
                        <Button size="sm" variant="outline" onClick={() => startEdit(v)} data-testid={`depot-edit-${v.id}`}><Pencil className="w-4 h-4" /></Button>
                        <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(v.id)} data-testid={`depot-delete-${v.id}`}><Trash2 className="w-4 h-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VenteDepot;
